import React, { useEffect, useState } from 'react';
import { Modal } from 'semantic-ui-react';
import styled from 'styled-components';
import FileViewer from '../../settings/repository-management/components/FileViewer';
import { PDFViewer } from '../../settings/repository-management/components/PDFViewer';
import VideoViewer from '../../settings/repository-management/components/VideoViewer';
import PositionedModal from 'common/components/PositionedModal';

interface PreviewFile {
  fileId?: string;
  fileUrl?: string;
  label?: string;
}

interface CopilotFilePreviewModalProps {
  file: PreviewFile | null;
  isOpen: boolean;
  onClose: () => void;
}

const getFileType = (fileUrl?: string, label?: string): string | null => {
  const value = fileUrl || label || '';
  const match = value.match(/\.([a-z0-9]+)(?:\?|#|$)/i);
  return match ? match[1].toLowerCase() : null;
};

const isSameOrigin = (url: string) => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
};

export const CopilotFilePreviewModal: React.FC<CopilotFilePreviewModalProps> = ({
  file,
  isOpen,
  onClose,
}) => {
  const [blob, setBlob] = useState<Blob | null>(null);
 const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedFileType, setResolvedFileType] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const initialFileType = getFileType(file?.fileUrl, file?.label);
  const title = file?.label || 'Resource Viewer';

  useEffect(() => {
    if (!isOpen || !file || file.fileId || !file.fileUrl) {
      setBlob(null);
      setLoading(false);
      setError(null);
      setResolvedFileType(null);
      setPreviewUrl(null);
      return;
    }

    const url = file.fileUrl;
    const fileTypeByUrl = getFileType(url, file.label);
    const wantsPdf = fileTypeByUrl === 'pdf';
    const wantsMp4 = fileTypeByUrl === 'mp4';

    if (wantsMp4) {
      setResolvedFileType('mp4');
      setBlob(null);
      setLoading(false);
      setError(null);
      setPreviewUrl(url);
      return;
    }

    let canceled = false;
    let createdObjectUrl: string | null = null;
    setLoading(true);
    setError(null);
    setResolvedFileType(null);
    setPreviewUrl(null);

    fetch(url, {
      method: 'GET',
      credentials: isSameOrigin(url) ? 'include' : 'omit',
    })
      .then(async response => {
        if (!response.ok) {
          throw new Error(`Failed to load file for preview: ${response.statusText}`);
        }

        const contentType = response.headers.get('Content-Type')?.toLowerCase() || '';
        const disposition = response.headers.get('Content-Disposition')?.toLowerCase() || '';
        const isPdf = contentType.includes('pdf') || disposition.includes('.pdf') || wantsPdf;
        const isMp4FromHeaders = contentType.includes('video/mp4') || disposition.includes('.mp4');

        const result = await response.blob();
        if (canceled) {
          return;
        }

        if (isPdf) {
          setBlob(result);
          setResolvedFileType('pdf');
          setPreviewUrl(null);
        } else if (isMp4FromHeaders) {
          createdObjectUrl = URL.createObjectURL(result);
          setPreviewUrl(createdObjectUrl);
          setResolvedFileType('mp4');
        } else {
          // Preserve the blob to allow fallback download if still needed.
          setBlob(result);
          setResolvedFileType('unknown');
        }
        setLoading(false);
      })
      .catch(err => {
        if (!canceled) {
          setError(err.message || 'Failed to load file for preview.');
          setLoading(false);
          setResolvedFileType(null);
          setPreviewUrl(null);
        }
      });

    return () => {
      canceled = true;
      if (createdObjectUrl) {
        URL.revokeObjectURL(createdObjectUrl);
      }
    };
  }, [file, isOpen]);

  const renderContent = () => {
    if (!file) {
      return <EmptyState>No resource selected.</EmptyState>;
    }

    if (file.fileId) {
      return <FileViewer fileId={file.fileId} isFullScreen={false} />;
    }

    if (!file.fileUrl) {
      return <EmptyState>Unable to preview this resource.</EmptyState>;
    }

    if (resolvedFileType === 'pdf') {
      if (loading) {
        return <LoadingState>Loading preview...</LoadingState>;
      }
      if (error) {
        return <EmptyState>{error}</EmptyState>;
      }
      if (blob) {
        return <PDFViewer blob={blob} isFullScreen={false} />;
      }
      return <LoadingState>Preparing preview...</LoadingState>;
    }

    if (resolvedFileType === 'mp4' && previewUrl) {
      return <VideoViewer fileUrl={previewUrl} isFullScreen={false} />;
    }

    if (resolvedFileType === 'mp4' && !previewUrl) {
      return <VideoViewer fileUrl={file.fileUrl} isFullScreen={false} />;
    }

    if (loading) {
      return <LoadingState>Loading preview...</LoadingState>;
    }

    if (error) {
      return <EmptyState>{error}</EmptyState>;
    }

    return (
      <FallbackState>
        <p>Unable to preview this resource in the application.</p>
        <a href={file.fileUrl} target="_blank" rel="noopener noreferrer">
          Open in a new tab
        </a>
      </FallbackState>
    );
  };

  return (
    <PositionedModal open={isOpen} onClose={onClose} size="large" closeIcon>
      <Modal.Header>{title}</Modal.Header>
      <Modal.Content style={{ minHeight: '600px', padding: 0 }}>
        <ModalBody>{renderContent()}</ModalBody>
      </Modal.Content>
    </PositionedModal>
  );
};

const ModalBody = styled.div`
  min-height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #ffffff;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: #2a3039;
  padding: 24px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: #6b7280;
  padding: 24px;
`;

const FallbackState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: #2a3039;
  padding: 24px;

  p {
    margin-bottom: 16px;
  }

  a {
    color: #2d70e2;
    text-decoration: underline;
  }
`;
